import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { canViewOrgWideEmployeeBenefits } from '../utils/employeeBenefitsAccess';
import { searchKindsForSelection, type TravelCanvasSelection } from '../utils/peopleTravelCanvasGraph';
import {
  seedTripCanvasIfEmpty,
  subscribePeopleTravelTrip,
  subscribeTripEvents,
  subscribeTripLegs,
  subscribeTripLocations,
  subscribeTripOptions,
  subscribeTripStops,
  updatePeopleTravelTrip,
} from '../utils/peopleTravelFirestore';
import {
  requestTripTravelSearch,
  subscribeCalendarSyncRequest,
  subscribeTravelSearchRequest,
  waitForTripTravelSearch,
} from '../utils/travelAgentService';
import type {
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelStop,
  PeopleTravelTrip,
} from '../types';
import PeopleTravelTripHeader from '../components/people/travel/PeopleTravelTripHeader';
import PeopleTravelPlannerBoard from '../components/people/travel/PeopleTravelPlannerBoard';
import PeopleTravelBudgetPanel from '../components/people/travel/PeopleTravelBudgetPanel';
import PeopleTravelCalendarSyncModal from '../components/people/travel/PeopleTravelCalendarSyncModal';
import PeopleTravelAgentStatusBanner from '../components/people/travel/PeopleTravelAgentStatusBanner';
import '../components/pm/pm-shared.css';
import '../components/people/travel/PeopleTravel.css';
import './OperationsHub.css';
import './PeopleHubPage.css';

const PeopleTravelTripPage: React.FC = () => {
  const { tripId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [trip, setTrip] = useState<PeopleTravelTrip | null>(null);
  const [options, setOptions] = useState<PeopleTravelOption[]>([]);
  const [stops, setStops] = useState<PeopleTravelStop[]>([]);
  const [locations, setLocations] = useState<PeopleTravelLocation[]>([]);
  const [events, setEvents] = useState<PeopleTravelEvent[]>([]);
  const [legs, setLegs] = useState<PeopleTravelLeg[]>([]);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchReq, setSearchReq] = useState<Record<string, unknown> | null>(null);
  const [calReq, setCalReq] = useState<Record<string, unknown> | null>(null);

  const canOrg = canViewOrgWideEmployeeBenefits({
    authRole: user?.role,
    designations: user?.designations,
  });

  const canEdit =
    !!trip &&
    !!user &&
    (trip.ownerUserId === user.id || canOrg);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribePeopleTravelTrip(tripId, t => {
      if (t === null) navigate('/people/travel');
      setTrip(t);
    });
  }, [tripId, navigate]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTripOptions(tripId, setOptions);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTripStops(tripId, setStops);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTripLocations(tripId, setLocations);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTripEvents(tripId, setEvents);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTripLegs(tripId, setLegs);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeTravelSearchRequest(tripId, setSearchReq);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId) return;
    return subscribeCalendarSyncRequest(tripId, setCalReq);
  }, [tripId]);

  React.useEffect(() => {
    if (!tripId || !canEdit) return;
    void seedTripCanvasIfEmpty(tripId).catch(() => undefined);
  }, [tripId, canEdit, trip?.destinations?.length, trip?.startDate]);

  const travelerLabels = useMemo(() => {
    if (!trip) return [];
    const map = new Map(crmUsers.map(u => [u.uid, u.displayName || u.email || u.uid]));
    return trip.travelerUserIds.map(uid => map.get(uid) || uid);
  }, [trip, crmUsers]);

  const defaultInvitees = useMemo(() => {
    const emails = trip?.travelerUserIds
      .map(uid => crmUsers.find(u => u.uid === uid)?.email)
      .filter((e): e is string => !!e);
    return emails || [];
  }, [trip, crmUsers]);

  const selectedOptions = useMemo(() => options.filter(o => o.isSelected), [options]);

  const runSearch = async (selection: TravelCanvasSelection | null) => {
    if (!tripId || searchBusy) return;
    setSearchBusy(true);
    try {
      await updatePeopleTravelTrip(tripId, { status: 'searching' });
      const kinds = searchKindsForSelection(selection, legs, events);
      await requestTripTravelSearch(tripId, kinds, {
        ...(selection?.legId ? { legId: selection.legId } : {}),
        ...(selection?.eventId ? { eventId: selection.eventId } : {}),
        ...(selection?.locationId ? { locationId: selection.locationId } : {}),
      });
      const outcome = await waitForTripTravelSearch(tripId);
      if (outcome.status === 'failed') {
        throw new Error(outcome.error || 'Travel search failed');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setSearchBusy(false);
    }
  };

  if (!user || !trip) {
    return (
      <div className="people-hub-route-root">
        <p className="people-hub-muted" style={{ padding: 24 }}>
          Loading trip…
        </p>
      </div>
    );
  }

  return (
    <div className="people-hub-route-root people-travel-trip-root">
      <PeopleTravelTripHeader
        trip={trip}
        travelerLabels={travelerLabels}
        onOpenBudget={() => setBudgetOpen(true)}
        onOpenCalendar={canOrg ? () => setSyncOpen(true) : undefined}
      />

      <PeopleTravelAgentStatusBanner
        searchStatus={(searchReq?.status as string) || null}
        searchError={(searchReq?.error as string) || undefined}
        calendarStatus={(calReq?.status as string) || trip.calendarSyncStatus || null}
        calendarError={(calReq?.error as string) || trip.calendarSyncError}
      />

      <PeopleTravelPlannerBoard
        trip={trip}
        ownerUserId={user.id}
        readOnly={!canEdit}
        locations={locations}
        events={events}
        legs={legs}
        stops={stops}
        options={options}
        searchBusy={searchBusy}
        onSearchSelected={selection => void runSearch(selection)}
      />

      {budgetOpen && (
        <div className="ops-modal-overlay" role="presentation" onClick={() => setBudgetOpen(false)}>
          <div
            className="ops-modal people-travel-create-modal"
            role="dialog"
            aria-labelledby="people-travel-budget-title"
            onClick={e => e.stopPropagation()}
          >
            <div className="ops-modal-header">
              <h3 id="people-travel-budget-title">Trip budget</h3>
              <button
                type="button"
                className="ops-modal-close"
                aria-label="Close"
                onClick={() => setBudgetOpen(false)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="ops-modal-body">
              <PeopleTravelBudgetPanel trip={trip} options={options} />
            </div>
          </div>
        </div>
      )}

      <PeopleTravelCalendarSyncModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        trip={trip}
        stops={stops}
        locations={locations}
        events={events}
        legs={legs}
        selectedOptions={selectedOptions.length ? selectedOptions : options}
        defaultInvitees={defaultInvitees}
        primaryCalendarId="primary"
        onSynced={() => setSyncOpen(false)}
      />
    </div>
  );
};

export default PeopleTravelTripPage;
